// The copies of a site plan drawn on it and saved to the job's SharePoint site
// beside the plain one: the marked-up copy (Site plan (marked up).jpg, the
// pins and arrows) and the access copy (Site plan (access).jpg, the access
// lines and no pins: access-plan.ts). The plain plan stays as it was saved,
// so pins and lines can keep changing. A copy is saved only when the measurer
// taps Save (each save is a SharePoint version, so not on every change); the
// visit records what it shows (hig_siteplan.markedUp / .access, a
// fingerprint), so every iPad can say when it's out of date.
import { useState } from 'react';
import { uploadSitePlanApi } from './api';
import { getRecord, patchFields, type DeviceRecord, type Fields } from './device-db';
import { loadImageUrl, planVersion, toJpeg, type SitePlanDoc } from './site-plan';
import { pinShape, pinsFingerprint } from './site-plan-math';

/** One photo's pin: its number and colour (its area's), where it is, and the way the camera faced. */
export interface PlanPin {
  id: string;
  number: number;
  colour: string;
  x: number | null;
  y: number | null;
  bearing: number | null;
}

/** A pin's white edge and number. */
export const PIN_EDGE = '#ffffff';
export const PIN_FONT = 'Roboto, Arial, sans-serif';

/** None saved for this plan yet; saved and showing today's pins (or lines); or saved before they last changed. */
export type MarkedUpStatus = 'none' | 'current' | 'outOfDate';

/** Which copy: where the visit records it (hig_siteplan), and the API's `copy`. */
export type PlanCopyKind = 'markedUp' | 'access';
const API_COPY: Record<PlanCopyKind, 'marked' | 'access'> = { markedUp: 'marked', access: 'access' };

/** What a copy records about itself on the visit: its link, when, and the fingerprint of what it shows. */
interface SavedCopy {
  webUrl: string;
  savedOn: string;
  fingerprint: string | undefined;
}

/** A copy's record on the plan, whichever kind (markedUp keeps its fingerprint as `pins`, access as `lines`). */
function savedCopy(plan: SitePlanDoc | null, kind: PlanCopyKind): SavedCopy | null {
  if (kind === 'markedUp') return plan?.markedUp ? { ...plan.markedUp, fingerprint: plan.markedUp.pins } : null;
  return plan?.access ? { ...plan.access, fingerprint: plan.access.lines } : null;
}

/** Whether a plan's copy shows what `fingerprint` describes. */
function copyStatus(plan: SitePlanDoc, kind: PlanCopyKind, fingerprint: string): MarkedUpStatus {
  const saved = savedCopy(plan, kind);
  if (!saved) return 'none';
  return saved.fingerprint === fingerprint ? 'current' : 'outOfDate';
}

/** A blank canvas the plan's own size, with the plan drawn on it. */
export async function planCanvas(image: string, plan: SitePlanDoc): Promise<{ canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D }> {
  const canvas = document.createElement('canvas');
  canvas.width = plan.width;
  canvas.height = plan.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This device can’t draw the plan.');
  ctx.drawImage(await loadImageUrl(image), 0, 0, plan.width, plan.height);
  return { canvas, ctx };
}

/** Draw the pins (and arrows, where a pin has a facing) onto the plan, as a JPEG the plan's own size. */
async function drawMarkedUpPlan(image: string, plan: SitePlanDoc, pins: readonly PlanPin[]): Promise<Blob> {
  const { canvas, ctx } = await planCanvas(image, plan);
  for (const pin of pins) {
    if (pin.x === null || pin.y === null) continue;
    const centre = { x: pin.x * plan.width, y: pin.y * plan.height };
    const shape = pinShape(plan.width, centre, pin.bearing ?? 0);
    ctx.fillStyle = pin.colour;
    ctx.strokeStyle = PIN_EDGE;
    if (pin.bearing !== null) {
      ctx.beginPath();
      shape.arrow.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
      ctx.closePath();
      ctx.lineWidth = shape.arrowEdge;
      ctx.fill();
      ctx.stroke();
    }
    ctx.beginPath();
    ctx.arc(centre.x, centre.y, shape.r, 0, 2 * Math.PI);
    ctx.lineWidth = shape.ringWidth;
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = PIN_EDGE;
    ctx.font = `700 ${shape.r}px ${PIN_FONT}`;
    ctx.textAlign = 'center';
    ctx.fillText(String(pin.number), centre.x, shape.textY);
  }
  return toJpeg(canvas);
}

/** The plan a visit's stored fields hold now, or null. */
const storedPlan = (fields: Fields): SitePlanDoc | null => (typeof fields.hig_siteplan === 'string' ? (JSON.parse(fields.hig_siteplan) as SitePlanDoc) : null);

/** Record the saved copy on the visit, unless the plan was remade meanwhile (that copy is of the old plan). */
async function noteCopy(visitId: string, version: string, kind: PlanCopyKind, copy: SavedCopy): Promise<void> {
  const record = kind === 'markedUp' ? { markedUp: { webUrl: copy.webUrl, savedOn: copy.savedOn, pins: copy.fingerprint } } : { access: { webUrl: copy.webUrl, savedOn: copy.savedOn, lines: copy.fingerprint } };
  await patchFields(visitId, (stored): Fields => {
    const doc = storedPlan(stored);
    return doc && planVersion(doc) === version ? { hig_siteplan: JSON.stringify({ ...doc, ...record }) } : {};
  });
}

/** Refuse to send a copy of a plan this device no longer holds (remade here, or on another iPad and synced here). */
async function assertStillThePlan(visitId: string, version: string): Promise<void> {
  const visit = await getRecord(visitId);
  const doc = visit ? storedPlan(visit.fields) : null;
  if (!doc || planVersion(doc) !== version) throw new Error('The plan was remade while the copy was being drawn. Save the copy again.');
}

/** A copy's state for the plan on screen, and Save to draw and save it (needs signal). `fingerprint`: of what it would show now. */
export function usePlanCopy(visit: DeviceRecord | undefined, plan: SitePlanDoc | null, image: string | null, kind: PlanCopyKind, fingerprint: string | null, draw: (image: string, plan: SitePlanDoc) => Promise<Blob>) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const status: MarkedUpStatus = plan && fingerprint ? copyStatus(plan, kind, fingerprint) : 'none';
  const saved = savedCopy(plan, kind);

  async function save() {
    if (!visit || !plan || !image || !fingerprint) return;
    setSaving(true);
    setError(null);
    try {
      const copy = await draw(image, plan);
      // Checked here and again by the API (against CRM): an upload of the old plan would replace a newer plan's copy (PR #489).
      await assertStillThePlan(visit.id, planVersion(plan));
      const uploaded = await uploadSitePlanApi(visit.id, copy, { copy: API_COPY[kind], planSavedOn: plan.savedOn });
      await noteCopy(visit.id, planVersion(plan), kind, { webUrl: uploaded.webUrl, savedOn: new Date().toISOString(), fingerprint });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return { status, webUrl: saved?.webUrl ?? null, savedOn: saved?.savedOn ?? null, saving, error, ready: Boolean(image), save };
}

/** The marked-up copy's state for the plan and pins on screen, and Save to draw and save it (needs signal). */
export function useMarkedUpPlan(visit: DeviceRecord | undefined, plan: SitePlanDoc | null, image: string | null, pins: readonly PlanPin[]) {
  const fingerprint = plan ? pinsFingerprint(planVersion(plan), pins) : null;
  return usePlanCopy(visit, plan, image, 'markedUp', fingerprint, (img, p) => drawMarkedUpPlan(img, p, pins));
}
